import React from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SlidersHorizontal } from "lucide-react";
import { Layout } from "../components/Layout";
import { SponsorSlot } from "../components/SponsorSlot";
import { ExperienceCard } from "../components/ExperienceCard";
import { CategoryIcon } from "../components/CategoryIcon";
import { useI18n } from "../i18n";
import { catName } from "../lib/format";
import * as apiClient from "../api";

export default function Experiences() {
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const q = params.get("q") || "";
  const people = params.get("people") || "";
  const sort = params.get("sort") || "ranking";

  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: apiClient.getCategories });
  const { data: experiences = [], isLoading } = useQuery({
    queryKey: ["experiences", category, q, people, sort],
    queryFn: () => apiClient.getExperiences({ category: category || undefined, q: q || undefined, people: people || undefined, sort }),
  });

  const setParam = (key, val) => {
    const p = new URLSearchParams(params);
    if (val) p.set(key, val); else p.delete(key);
    setParams(p);
  };

  const activeCat = categories.find((c) => c.slug === category);

  const emptyCopy = (slug = "") => {
    const s = slug.toLowerCase();
    if (/(grott|barca|boat|tremiti|cave)/.test(s)) return "Stiamo convincendo i capitani. Le prossime avventure stanno arrivando.";
    if (/(quad|jeep|4x4)/.test(s)) return "Stiamo scegliendo quelli che sporcano meglio. Torna presto.";
    if (/(food|gust|sapor|cibo|wine|vino|cucina)/.test(s)) return "Stiamo assaggiando tutto. È un lavoro duro, ma qualcuno deve farlo.";
    if (/(natur|foresta|umbra|trekk|hik)/.test(s)) return "Ci siamo persi nella Foresta Umbra. Torniamo presto.";
    if (/(caval|horse|equi)/.test(s)) return "Stiamo sellando.";
    if (/(bike|bici|cicl|mtb)/.test(s)) return "Stiamo gonfiando le gomme.";
    if (/(div|sub|snorkel|mare|sea)/.test(s)) return "Stiamo andando a fondo. Nel senso buono.";
    if (/(mont|dolom|marmolad|neve|ski|sci)/.test(s)) return "Stiamo salendo. Letteralmente.";
    return "Qui sta per succedere qualcosa. Torna presto.";
  };

  return (
    <Layout>
      <div className="bg-gradient-to-b from-slate-50 to-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-2">{t("exp.list.eyebrow")}</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-semibold tracking-tight text-slate-900">
            {activeCat ? catName(activeCat, lang) : q ? `"${q}"` : t("exp.list.all")}
          </h1>
          <div className="mt-6 flex gap-2 overflow-x-auto no-scrollbar pb-2" data-testid="category-filter-bar">
            <button onClick={() => setParam("category", "")} className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium transition-colors ${!category ? "bg-primary text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-primary"}`}>
              {t("search.all")}
            </button>
            {categories.map((c) => (
              <button key={c.slug} data-testid={`filter-cat-${c.slug}`} onClick={() => setParam("category", c.slug)} className={`shrink-0 flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${category === c.slug ? "bg-primary text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-primary"}`}>
                <CategoryIcon name={c.icon} className="h-4 w-4" /> {catName(c, lang)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {experiences.length > 0 && (
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-slate-500">{experiences.length} {t("common.results")}</p>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <SlidersHorizontal className="h-4 w-4" />
              <select data-testid="sort-select" value={sort} onChange={(e) => setParam("sort", e.target.value)} className="bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-medium outline-none focus:ring-2 focus:ring-secondary">
                <option value="ranking">{t("sort.recommended")}</option>
                <option value="rating">{t("sort.rating")}</option>
                <option value="price_asc">{t("sort.priceasc")}</option>
                <option value="price_desc">{t("sort.pricedesc")}</option>
              </select>
            </label>
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-20 text-slate-400">{t("common.loading")}</div>
        ) : experiences.length === 0 ? (
          <div className="text-center py-24 max-w-lg mx-auto animate-float-up" data-testid="experiences-empty-state">
            <div className="h-20 w-20 rounded-3xl bg-primary/8 text-primary flex items-center justify-center mx-auto mb-6">
              <CategoryIcon name={activeCat?.icon || "Sparkles"} className="h-9 w-9" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight text-slate-900">
              {activeCat ? catName(activeCat, lang) : "Cristalda Experience"}
            </h2>
            <p className="mt-3 text-lg font-accent italic text-secondary">{emptyCopy(category)}</p>
            <p className="mt-4 text-sm text-slate-500">Stiamo selezionando gli operatori migliori del Gargano. Le esperienze reali arriveranno presto.</p>
            <button onClick={() => setParam("category", "")} className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary text-white font-semibold px-7 py-3 hover:bg-primary/90 transition-colors" data-testid="empty-back-categories">
              Vedi tutte le categorie
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {experiences.map((e, i) => <ExperienceCard key={e.id} exp={e} index={i} />)}
          </div>
        )}
      </div>
      <SponsorSlot placement="ESPERIENZE" className="pb-14" />
    </Layout>
  );
}
